import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Lock, FileText, Wrench, Rocket, Check, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import ScoreGauge from "@/components/ScoreGauge";
import MigrationPitch from "@/components/MigrationPitch";
import { computeScope } from "@/lib/hosting";

const SEV = { critical: "#FF3B30", high: "#FF7A45", warning: "#FFD60A", info: "#4da3ff" };

// Free gated report: overall score + a few sample issue titles + a project-size estimate.
export const GatedReport = ({ data, scores: s, comp, sec, seo, onPurchase }) => {
  const navigate = useNavigate();
  const vals = [s.frontend, s.mobile, s.security, s.seo, s.compliance, s.integration].filter((v) => typeof v === "number");
  const overall = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  const secGaps = [];
  if (!sec.hsts) secGaps.push({ title: "Missing HSTS security header", severity: "warning" });
  if (!sec.csp) secGaps.push({ title: "No Content-Security-Policy set", severity: "warning" });
  const rank = { critical: 0, high: 1, warning: 2, info: 3 };
  const allIssues = [...(comp.flags || []), ...(seo.flags || []), ...secGaps]
    .sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9));
  const sample = allIssues.slice(0, 3);
  const total = allIssues.length;
  const scope = computeScope({ data, comp, seo, secGaps });
  const { key: scopeKey, emoji: scopeEmoji, label: scopeLabel, color: scopeColor } = scope;
  const quoteHref = `/pricing?domain=${encodeURIComponent(data.domain)}&scope=${scopeKey}#intake`;

  const help = [];
  if (!sec.hsts || !sec.csp) help.push({ t: "SSL & security hardening", d: "HTTPS best-practices, HSTS and a Content-Security-Policy." });
  if ((seo.flags || []).length) help.push({ t: "SEO-safe move with 301 redirects", d: "Keep your rankings — we map old URLs and fix broken links." });
  if ((comp.flags || []).length) help.push({ t: "Privacy & analytics setup", d: "Tracker/cookie compliance plus Analytics & Search Console." });
  help.push({ t: "Domain, DNS & email move", d: "Transfer your domain, DNS records and business email with zero downtime." });
  help.push({ t: "Managed hosting & monitoring", d: "Host on Emergent with backups and uptime monitoring." });
  const helpTop = help.slice(0, 4);

  return (
    <Layout>
      <button data-testid="back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-5 transition-colors">
        <ArrowLeft size={16} /> Back to dashboard
      </button>

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-white">{data.domain}</h1>
        <p className="font-mono text-sm text-zinc-500 mt-1">
          {data.reachable ? <span className="text-[#32D74B]">● reachable</span> : <span className="text-[#FF3B30]">● unreachable</span>}
          {"  ·  "}{data.finalUrl}
        </p>
      </div>

      {/* Overall score + issue count */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6" data-testid="gated-summary">
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center">
          <ScoreGauge value={overall} label="Overall health" />
        </div>
        <div className="md:col-span-2 bg-[#121212] border border-white/10 rounded-xl p-6 flex flex-col justify-center">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-5xl font-extrabold text-white tabular-nums" data-testid="gated-issue-count">{total}</span>
            <span className="text-zinc-400 text-sm">issue{total === 1 ? "" : "s"} found</span>
          </div>
          <p className="text-zinc-400 text-sm mt-2 leading-relaxed max-w-lg">
            We scanned <b className="text-white">{data.domain}</b> across security, privacy, SEO, mobile and frontend. Here's a preview — unlock the full report to see every issue, the exact fixes, and an AI action plan.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 self-start" style={{ borderColor: `${scopeColor}55`, background: `${scopeColor}12` }} data-testid="scope-estimate">
            <span>{scopeEmoji}</span>
            <span className="text-sm font-semibold text-white">Estimated: {scopeLabel}</span>
          </div>
        </div>
      </div>

      {/* Personalized migration pitch — detected host + dependencies + savings */}
      <MigrationPitch data={data} comp={comp} seo={seo} sec={sec} showEmail={false} />

      {/* Sample issue titles (no detail) */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="gated-sample-issues">
        <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-3">Sample of what we found</h3>
        {sample.length ? (
          <div className="space-y-2">
            {sample.map((f, i) => (
              <div key={i} data-testid={`gated-issue-${i}`} className="flex items-center gap-2 border border-white/10 rounded-lg px-3.5 py-3" style={{ background: `${SEV[f.severity] || "#4da3ff"}0a` }}>
                <ShieldAlert size={14} style={{ color: SEV[f.severity] || "#4da3ff" }} />
                <span className="text-white text-sm font-medium">{f.title}</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[f.severity] || "#4da3ff", background: `${SEV[f.severity] || "#4da3ff"}1a` }}>{f.severity}</span>
              </div>
            ))}
            {total > sample.length && (
              <div className="flex items-center gap-2 border border-dashed border-white/15 rounded-lg px-3.5 py-3 text-zinc-500" data-testid="gated-more-locked">
                <Lock size={14} /> <span className="text-sm">+ {total - sample.length} more issue{total - sample.length === 1 ? "" : "s"} — fixes locked</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-[#32D74B] text-sm">No major issues in the free preview. Unlock the full report for the complete audit.</p>
        )}
      </div>

      {/* What we can move & fix for you (personalized) */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="help-card">
        <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-3">What we can move &amp; fix for you</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {helpTop.map((h, i) => (
            <div key={i} data-testid={`help-item-${i}`} className="flex gap-2.5 border border-white/10 rounded-lg px-3.5 py-3">
              <Check size={15} className="text-[#32D74B] mt-0.5 shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">{h.t}</p>
                <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">{h.d}</p>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="help-quote-btn" onClick={() => navigate(quoteHref)} className="mt-4 text-[#4da3ff] hover:text-white text-sm inline-flex items-center gap-1.5 transition-colors">
          Get a free quote to handle this ({scopeEmoji} {scopeLabel}) <ArrowRight size={14} />
        </button>
      </div>

      {/* Unlock CTA */}
      <div className="relative overflow-hidden rounded-2xl border border-[#4da3ff]/30 p-8 text-center" style={{ background: "linear-gradient(135deg, rgba(0,122,255,0.14), rgba(155,92,255,0.14))" }} data-testid="unlock-cta">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#4da3ff]/10 blur-3xl pointer-events-none" />
        <div className="inline-flex items-center gap-2 text-[#4da3ff] mb-3">
          <Lock size={16} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Full report locked</span>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight max-w-xl mx-auto">Get every issue, the exact fixes, and an AI build brief</h2>
        <p className="text-zinc-300 text-sm mt-3 max-w-lg mx-auto leading-relaxed">
          Unlock the full report for <b className="text-white">{data.domain}</b> — every detailed finding, the step-by-step fixes, an AI build brief and a downloadable PDF. One-time, just for this website. Or have us implement the fixes for you.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <button data-testid="unlock-report-btn" onClick={() => onPurchase("ai_report")} className="btn-brand text-white rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2">
            <FileText size={16} /> Unlock report + AI brief — $29
          </button>
          <button data-testid="guided-fix-btn" onClick={() => onPurchase("guided_fix")} className="bg-transparent border border-white/25 text-white hover:bg-white/10 rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors">
            <Wrench size={16} /> Have us fix it — from $99
          </button>
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-[#32D74B]" data-testid="gated-bundle-badge">
          <Check size={13} /> AI Build Brief included with your $29 report
        </div>
        <button data-testid="migrate-emergent-btn" onClick={() => navigate(quoteHref)} className="mt-4 text-zinc-300 hover:text-white text-sm inline-flex items-center gap-1.5 transition-colors">
          <Rocket size={14} /> Or move/upgrade your site — get a free quote ({scopeEmoji} {scopeLabel})
        </button>
      </div>
    </Layout>
  );
};

export default GatedReport;
